import { useEffect, useImperativeHandle, useMemo, useRef } from "react";

import type { Editor } from "@tiptap/core";
import { Markdown } from "@tiptap/markdown";
import { useEditor } from "@tiptap/react";

import { useThrottle } from "simplydo/react/hooks";

import { EditorContext } from "./EditorContext";
import { createMenuCommands } from "./commands";
import {
  DEFAULT_CONTENT,
  getEditorPlainText,
  normaliseIncomingHtml,
  sanitiseValue,
} from "./editorContent";
import type { InnerEditorProps, Uploader } from "./editorTypes";

/** Transactions we apply ourselves are tagged so they aren't reported back as user edits. */
const PROGRAMMATIC_UPDATE = "richtextProgrammaticUpdate";

const isEmptyContent = (html: string) =>
  html === DEFAULT_CONTENT || html === "" || html === "<p></p>";

const getFilesFromDataTransfer = (dataTransfer: DataTransfer | null) =>
  Array.from(dataTransfer?.files ?? []);

const isImage = (file: File) => file.type.startsWith("image/");

/** Uploads pasted or dropped files and inserts them as image or file nodes. */
const insertUploads = (
  editor: Editor,
  files: File[],
  position: number | null,
  uploaders: { uploadImage?: Uploader | null; uploadFile?: Uploader | null },
) => {
  let handled = false;

  for (const file of files) {
    const asImage =
      isImage(file) && !!uploaders.uploadImage && !!editor.schema.nodes.image;
    const uploader = asImage
      ? uploaders.uploadImage
      : (uploaders.uploadFile ?? uploaders.uploadImage);
    if (!uploader || (!asImage && !editor.schema.nodes.file)) {
      continue;
    }

    handled = true;
    const nodeType = asImage ? "image" : "file";
    uploader(file)
      .then(({ src, name }) => {
        if (editor.isDestroyed) {
          return;
        }
        const content =
          nodeType === "image"
            ? { type: "image", attrs: { src, alt: name, title: name } }
            : { type: "file", attrs: { src, name } };
        if (position === null) {
          editor.chain().focus().insertContent(content).run();
        } else {
          editor.chain().focus().insertContentAt(position, content).run();
        }
      })
      .catch((error) => {
        console.error("Error uploading file: ", error);
      });
  }

  return handled;
};

const EditorWrapper = ({
  value,
  onChange,
  onBlur,
  onFocus,
  emptyReturnValue = DEFAULT_CONTENT,
  extensions = [],
  children,
  readOnly,
  editorRef,
  autoFocus,
  markdown,
  settings,
  onLinkClick,
  uploadImage,
  uploadFile,
}: InnerEditorProps) => {
  const outsideValueSanitised = useMemo(() => {
    if (markdown) {
      return value ?? "";
    }
    const raw = value ?? emptyReturnValue ?? "";
    return sanitiseValue(normaliseIncomingHtml(raw) || DEFAULT_CONTENT);
  }, [markdown, value, emptyReturnValue]);

  // Maintain a previous content state to prevent unnecessary updates
  const previousContent = useRef<string | undefined>(outsideValueSanitised);

  const outsideValue = outsideValueSanitised;
  const isReadOnly = !!readOnly;

  // Throttle protects poorly-memoised parents that re-render on every keystroke.
  // Prefer debouncing at the call site when possible; this is a safety net.
  const emitChange = useThrottle(
    (html: string, text: string) => {
      onChange?.(html, text);
      previousContent.current = html;
    },
    250,
    [onChange],
  );

  const handlers = {
    onFocus,
    onBlur,
    emitChange,
    emptyReturnValue,
    onLinkClick,
    uploadImage,
    uploadFile,
  };

  // The editor instance is needed inside the ProseMirror handlers below, which are created before
  // `useEditor` returns.
  const instanceRef = useRef<Editor | null>(null);

  const editorExtensions = useMemo(
    () => (markdown ? [...extensions, Markdown] : extensions),
    [extensions, markdown],
  );

  const editor = useEditor({
    extensions: editorExtensions,
    content: outsideValueSanitised,
    ...(markdown ? { contentType: "markdown" as const } : {}),
    editable: !readOnly,
    autofocus: autoFocus ? "end" : false,
    parseOptions: { preserveWhitespace: "full" },
    editorProps: {
      attributes: { class: "richtext-editor" },
      handleClick: (_view, _pos, event) => {
        const onClick = handlers.onLinkClick;
        if (!onClick) {
          return false;
        }
        const target = event.target as HTMLElement | null;
        const anchor = target?.closest?.("a[href]") as HTMLAnchorElement | null;
        const href = anchor?.getAttribute("href");
        if (!href) {
          return false;
        }
        // File chips are also <a href> for copy-link, but they have their own
        // download handler — do not treat them as navigable / external links.
        if (anchor?.getAttribute("data-ext") === "file") {
          return false;
        }
        // Always stop the browser from following target=_blank before our modal /
        // SPA navigation runs — including when the click landed on nested markup.
        event.preventDefault();
        return onClick(href, event);
      },
      handlePaste: (_view, event) => {
        const instance = instanceRef.current;
        const files = getFilesFromDataTransfer(event.clipboardData);
        if (!instance || files.length === 0 || isReadOnly) {
          return false;
        }
        return insertUploads(instance, files, null, handlers);
      },
      handleDrop: (view, event, _slice, moved) => {
        const instance = instanceRef.current;
        const files = getFilesFromDataTransfer(
          (event as DragEvent).dataTransfer,
        );
        if (!instance || moved || files.length === 0 || isReadOnly) {
          return false;
        }
        const dropEvent = event as DragEvent;
        const position =
          view.posAtCoords({ left: dropEvent.clientX, top: dropEvent.clientY })
            ?.pos ?? null;
        return insertUploads(instance, files, position, handlers);
      },
    },
    onFocus: ({ event }) => {
      handlers.onFocus?.(event);
    },
    onBlur: ({ editor: instance }) => {
      if (isReadOnly) {
        return;
      }
      const blurHandler = handlers.onBlur;
      if (!blurHandler) {
        return;
      }
      const html = instance.getHTML();
      if (isEmptyContent(html)) {
        blurHandler(handlers.emptyReturnValue ?? "", "");
        return;
      }
      blurHandler(sanitiseValue(html), getEditorPlainText(instance));
    },
    onUpdate: ({ editor: instance, transaction }) => {
      // The external change handler is only called when the actual HTML content changes,
      // so a few checks are necessary to reduce unnecessary calls
      if (isReadOnly) return;
      if (transaction.getMeta(PROGRAMMATIC_UPDATE)) return;
      if (transaction.steps.length === 0) return;

      const html = instance.getHTML();
      const sanitised = sanitiseValue(html);
      if (outsideValue === sanitised) return;

      if (isEmptyContent(html)) {
        handlers.emitChange(handlers.emptyReturnValue ?? "", "");
      } else {
        handlers.emitChange(sanitised, getEditorPlainText(instance));
      }
    },
  });

  useEffect(() => {
    instanceRef.current = (editor as Editor | null) ?? null;
    if (editor && editor.isEditable !== !readOnly) {
      editor.setEditable(!readOnly, false);
    }
  }, [editor, readOnly]);

  useEffect(() => {
    if (!editor) {
      return;
    }

    // If the outside value is the same as the current value then we don't need to update the editor
    // This is especially important to prevent an infinite loop when not all props are nicely memoized
    const outside = outsideValueSanitised;
    if (outside === previousContent.current) {
      return;
    }

    if (markdown) {
      editor
        .chain()
        .setMeta(PROGRAMMATIC_UPDATE, true)
        .setContent(outside, { contentType: "markdown", emitUpdate: false })
        .run();
      previousContent.current = outside;
      return;
    }

    // Fast path: TipTap HTML often already matches the sanitised outside value.
    // Fall back to sanitising only when they differ (attribute order / whitespace).
    const rawHtml = editor.getHTML();
    if (rawHtml === outside || sanitiseValue(rawHtml) === outside) {
      previousContent.current = outside;
      return;
    }

    const { from, to } = editor.state.selection;
    editor
      .chain()
      .setMeta(PROGRAMMATIC_UPDATE, true)
      .setContent(outside, {
        emitUpdate: false,
        parseOptions: { preserveWhitespace: "full" },
      })
      .run();

    if (!readOnly && outside !== DEFAULT_CONTENT) {
      const size = editor.state.doc.content.size;
      try {
        editor.commands.setTextSelection({
          from: Math.min(from, size),
          to: Math.min(to, size),
        });
      } catch {
        // The new document may not have a valid text position at the old selection.
      }
    }

    previousContent.current = outside;
  }, [outsideValueSanitised, editor, readOnly, markdown]);

  useImperativeHandle(
    editorRef,
    () => ({
      editor,
      focus: (position?: number | "start" | "end" | "all") => {
        editor
          ?.chain()
          .focus(position ?? "end")
          .run();
      },
      blur: () => {
        editor?.commands.blur();
      },
      getHTML: () => (editor ? sanitiseValue(editor.getHTML()) : ""),
      getJSON: () => editor?.getJSON() ?? null,
      getText: () => (editor ? getEditorPlainText(editor) : ""),
    }),
    [editor],
  );

  const contextValue = useMemo(
    () => ({
      editor: (editor as Editor | null) ?? null,
      isEditable: !readOnly,
      settings: settings ?? {},
      commands: createMenuCommands((editor as Editor | null) ?? null),
    }),
    [editor, readOnly, settings],
  );

  return (
    <EditorContext.Provider value={contextValue}>
      <div
        style={{
          flex: 1,
          alignSelf: "stretch",
          position: "relative",
          wordBreak: "break-all",
          minHeight: "100px !important",
        }}
      >
        {children}
      </div>
    </EditorContext.Provider>
  );
};

export default EditorWrapper;
